import { displayDate } from './dateUtil.js'
// src/lib/replyContext.js
// One answer to "what does replying to this message mean" — recipients, subject,
// quoted body, and the Gmail threading ids. Pure functions: no Supabase, no React,
// no browser APIs, so `api/` can import it too (same direction as src/lib/nameMatch.js
// and src/lib/dealMergeArchive.js — client-side helpers live in src/, api/ imports them).
//
// Used by:
//   • the inbox Reply / Reply All handlers (InboxIntelligencePage)
//   • JanetDraftModal, whenever it is drafting a REPLY rather than a new email
//   • api/janet-task-suggest.js, which builds the reply target for a task
//
// A Janet draft that answers an inbound message must land IN that thread, on the
// same recipients. Sending it as a fresh email splits the conversation in the
// counterparty's mailbox — which for a negotiation means the response to an offer
// arrives detached from the offer.

// to_emails / cc_emails are stored as {name, email} objects (parseEmailList in
// poll-inbox.js), but legacy rows still hold plain strings and some rows carry
// garbage from the pre-fix parser (display names bisected on a comma). Requiring
// '@' drops those.
export function normalizeRecipient(entry) {
  if (!entry) return null
  if (typeof entry === 'string') {
    const email = entry.trim()
    return email.includes('@') ? { email, name: '' } : null
  }
  if (entry.email && String(entry.email).includes('@')) {
    return { email: String(entry.email).trim(), name: entry.name || '' }
  }
  return null
}

export function normalizeRecipients(list) {
  return (Array.isArray(list) ? list : []).map(normalizeRecipient).filter(Boolean)
}

const lc = (s) => (s || '').toLowerCase().trim()

export function dedupeRecipients(list) {
  return (list || []).reduce((acc, r) => {
    if (r?.email && !acc.find(x => lc(x.email) === lc(r.email))) acc.push(r)
    return acc
  }, [])
}

/**
 * Reply-all recipient split for one message.
 *
 * To = original sender + everyone in the original To.
 * Cc = everyone in the original Cc.
 * Both minus `exclude` (the sending mailbox — and janet@ when Janet is the sender,
 * so she never Ccs herself).
 *
 * When the message being replied to is one WE sent (is_outbound), the sender is
 * us: it gets excluded by the same rule, leaving the original recipients intact.
 */
export function buildReplyAllRecipients({
  fromEmail,
  fromName,
  toEmails = [],
  ccEmails = [],
  exclude = [],
} = {}) {
  const blocked = new Set((Array.isArray(exclude) ? exclude : [exclude]).map(lc).filter(Boolean))
  const keep = (r) => r && !blocked.has(lc(r.email))

  const to = dedupeRecipients([
    fromEmail && String(fromEmail).includes('@') ? { email: fromEmail, name: fromName || '' } : null,
    ...normalizeRecipients(toEmails),
  ].filter(keep))

  const cc = dedupeRecipients(
    normalizeRecipients(ccEmails)
      .filter(keep)
      // Never Cc someone already in To.
      .filter(r => !to.find(t => lc(t.email) === lc(r.email)))
  )

  return { to, cc }
}

/** "Re: X" — collapses an existing Re: rather than stacking them. */
export function replySubject(subject) {
  const s = (subject || '').trim()
  if (!s) return ''
  return /^re:\s*/i.test(s) ? s.replace(/^re:\s*/i, 'Re: ') : `Re: ${s}`
}

/** "Name <email>, Other <email2>" — the string shape ComposeEmailModal + send-gmail take. */
export function recipientsToString(list) {
  return (list || [])
    .map(r => (r.name ? `${r.name} <${r.email}>` : r.email))
    .filter(Boolean)
    .join(', ')
}

/**
 * "a@x.com, b@y.com" — the bare-address form EmailRecipientField parses into
 * chips (it splits on commas, so "Name <addr>" pairs are not safe there).
 */
export function recipientsToEmails(list) {
  return (list || []).map(r => r.email).filter(Boolean).join(', ')
}

export function escapeHtml(s = '') {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** The "On <date>, <person> wrote:" block plus the original body. */
export function quotedReplyHtml({ fromName, fromEmail, sentAt, bodyHtml, bodyText } = {}) {
  const date = sentAt ? displayDate(sentAt).toLocaleString() : ''
  const header = fromEmail
    ? `On ${date}, ${fromName || fromEmail}${fromName && fromEmail ? ` &lt;${fromEmail}&gt;` : ''} wrote:`
    : 'Original message:'
  const bodyAsHtml = bodyHtml || (bodyText ? `<p>${escapeHtml(bodyText).replace(/\n/g, '<br>')}</p>` : '')
  return bodyAsHtml
    ? `<div class="chatrone-quoted-reply-header">${header}</div>${bodyAsHtml}`
    : ''
}

/**
 * Everything a draft surface needs to reply in-thread, from one reply target.
 *
 * replyTo: { gmailMessageId, gmailThreadId, subject, fromName, fromEmail, sentAt,
 *            toEmails, ccEmails, bodyHtml, bodyText, source }
 *
 * Returns null when there is nothing to reply to, or when the target is a
 * personal iCloud message — those never route through Gmail send and Janet is
 * blind to them by design (see CLAUDE.md, iCloud display-only).
 */
export function buildReplyPlan(replyTo, { exclude = [] } = {}) {
  if (!replyTo || !replyTo.gmailMessageId) return null
  if (replyTo.source === 'icloud' || String(replyTo.gmailMessageId).startsWith('icloud:')) return null

  const { to, cc } = buildReplyAllRecipients({
    fromEmail: replyTo.fromEmail,
    fromName:  replyTo.fromName,
    toEmails:  replyTo.toEmails,
    ccEmails:  replyTo.ccEmails,
    exclude,
  })

  return {
    to,
    cc,
    subject:    replySubject(replyTo.subject),
    inReplyTo:  replyTo.gmailMessageId,
    threadId:   replyTo.gmailThreadId || undefined,
    quotedHtml: quotedReplyHtml(replyTo),
  }
}
